import { FC, useCallback, useEffect } from 'react';
import { useFragment } from 'react-relay';
import { useField } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import ErrorIcon from '@mui/icons-material/Error';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import ConfirmableIconButton from 'components/ConfirmableIconButton';

import { useAvoidSuspense } from 'lib';

import Query, {
  TranslatableTextFieldTranslateButtonRefetchQuery,
} from '__generated__/TranslatableTextFieldTranslateButtonRefetchQuery.graphql';
import { TranslatableTextFieldTranslateButton_to$key } from '__generated__/TranslatableTextFieldTranslateButton_to.graphql';
import { TranslatableTextFieldTranslateButton_from$key } from '__generated__/TranslatableTextFieldTranslateButton_from.graphql';
import { TranslatableTextFieldTranslateButton_textTranslation$key } from '__generated__/TranslatableTextFieldTranslateButton_textTranslation.graphql';
import { TranslatableTextFieldTranslateButton_data$key } from '__generated__/TranslatableTextFieldTranslateButton_data.graphql';

type Props = {
  name: string;
  from: TranslatableTextFieldTranslateButton_from$key;
  to: TranslatableTextFieldTranslateButton_to$key;
  textTranslation: TranslatableTextFieldTranslateButton_textTranslation$key | null;
  data: TranslatableTextFieldTranslateButton_data$key;
  onTranslate: (translation: string) => void;
};

const TranslatableTextFieldTranslateButton: FC<Props> = props => {
  const { name, onTranslate, ...rest } = props;

  const from = useFragment(
    graphql`
      fragment TranslatableTextFieldTranslateButton_from on Language {
        locale

        ...LanguageIndicator_language
      }
    `,
    rest.from
  );

  const to = useFragment(
    graphql`
      fragment TranslatableTextFieldTranslateButton_to on Language {
        locale
        readOnly
      }
    `,
    rest.to
  );

  const textTranslation = useFragment(
    graphql`
      fragment TranslatableTextFieldTranslateButton_textTranslation on TextTranslation {
        id
        string
      }
    `,
    rest.textTranslation
  );

  const [{ translateTextTranslation }, translation] = useAvoidSuspense<
    TranslatableTextFieldTranslateButtonRefetchQuery,
    TranslatableTextFieldTranslateButton_data$key
  >(
    graphql`
      fragment TranslatableTextFieldTranslateButton_data on Query
      @refetchable(
        queryName: "TranslatableTextFieldTranslateButtonRefetchQuery"
      )
      @argumentDefinitions(
        translate: { type: "Boolean", defaultValue: false }
      ) {
        translateTextTranslation(id: $id, to: $to) @include(if: $translate)
      }
    `,
    rest.data,
    Query
  );

  const handleTranslateClick = useCallback(() => {
    if (!textTranslation) return;

    translation.refetch({
      id: textTranslation.id,
      to: to.locale,
      translate: true,
    });
  }, [textTranslation, to.locale, translation]);

  useEffect(() => {
    translateTextTranslation && onTranslate(translateTextTranslation);
  }, [onTranslate, translateTextTranslation]);

  const [{ value }] = useField<string>(`${name}.${to.locale}`);

  const disabled = !textTranslation?.string || to.readOnly;

  return (
    <Tooltip title={`Versti iš ${from.locale}`}>
      <span>
        <ConfirmableIconButton
          color="default"
          size="small"
          disabled={disabled}
          enabled={Boolean(value && value.length > 0) && !translation.loading}
          confirmIcon={<ErrorIcon fontSize="small" color="error" />}
          onConfirm={handleTranslateClick}>
          <Badge color="primary" badgeContent={from.locale.toLocaleLowerCase()}>
            {translation.loading ? (
              <CircularProgress size="1em" />
            ) : (
              <GTranslateIcon fontSize="small" />
            )}
          </Badge>
        </ConfirmableIconButton>
      </span>
    </Tooltip>
  );
};

export default TranslatableTextFieldTranslateButton;
